import isEmpty from 'lodash/isEmpty';
import keyBy from 'lodash/keyBy';
import get from 'lodash/get';
import isString from 'lodash/isString';
import { defineStore, acceptHMRUpdate } from 'pinia';
import {
    fetchSettings,
    fetchSetting,
    updateSetting,
    deleteSetting,
    parseValue,
} from '@/vuex/modules/user-settings/user-settings.utils';
import { useHistoryStore, HistoryItem } from '@/stores/history.store';
import { HISTORY_TYPES, RECENTLY_SELECTED_HISTORY_TYPES } from '@/vuex/modules/user-settings/history/history.constants';

interface UserSetting {
    name: string;
    value: unknown;
}

interface UserSettingsState {
    error: Error | null;
    fetching: boolean;
    map: Record<string, UserSetting>;
}

let loadAllPromise: Promise<unknown> | undefined;

function getInitialState (): UserSettingsState {
    return {
        error: null,
        fetching: false,
        map: {},
    };
}

export const useUserSettingsStore = defineStore('userSettings', {
    state: getInitialState,
    getters: {
        settingByName: (state) => (name: string, namespaced?: boolean): UserSetting | undefined => {
            // eslint-disable-next-line @typescript-eslint/no-require-imports
            const vuexStore = require('@/vuex/store').default;

            if (namespaced) {
                name += `--${vuexStore?.state.subscription.active.id}`;
            }

            return state.map[name];
        },
        crossUISettingByName: (state) => (name: string): UserSetting | undefined => {
            // eslint-disable-next-line @typescript-eslint/no-require-imports
            const vuexStore = require('@/vuex/store').default;
            const { active: subscription } = vuexStore?.state.subscription;
            const settingName = `${subscription.id}::${name}`;

            return state.map[settingName];
        },
    },
    actions: {
        async loadAll ({ reFetch = false }: { reFetch?: boolean } = {}) {
            if (!isEmpty(this.map) && !reFetch) {
                return;
            }

            if (reFetch) {
                loadAllPromise = undefined;
            }

            this.fetching = true;

            try {
                loadAllPromise ??= fetchSettings();
                const settings = await loadAllPromise;

                // eslint-disable-next-line @typescript-eslint/no-require-imports
                const vuexStore = require('@/vuex/store').default;
                const appsWithSuggestedPages = vuexStore?.getters['apps/appsWithSuggestedPages'];
                const canCreatePages = vuexStore?.getters['auth/canCreatePages'];
                const isAdmin = vuexStore?.getters['auth/isAdmin'];
                const { active: subscription } = vuexStore?.state.subscription;
                let uniqueSettings = (settings as UserSetting[]).reduce((acc: UserSetting[], setting: UserSetting) => {
                    const isDuplicate = acc.find(({ name }) => name === setting.name);

                    if (!isDuplicate) {
                        const parsedValue = parseValue(setting.value);

                        acc.push({
                            name: setting.name,
                            value: parsedValue,
                        });
                    }

                    return acc;
                }, []);
                const hasSuggestedPagesSetting = (canCreatePages || isAdmin) && appsWithSuggestedPages.length > 0;
                const userPreferencesName = `${subscription.id}::userPreferences`;
                const userPreferences: UserSetting = {
                    name: userPreferencesName,
                    value: {
                        ...(hasSuggestedPagesSetting && {
                            suggestedPagesTable: {
                                value: true,
                                disabled: false,
                                subSettings: {
                                    notifications: {
                                        value: true,
                                        disabled: false,
                                    },
                                },
                            },
                        }),
                        ...(get(
                            uniqueSettings.filter((setting: UserSetting) => setting.name === userPreferencesName),
                            '[0].value',
                            {},
                        ) as Record<string, unknown>),
                    },
                };
                uniqueSettings = uniqueSettings.filter((setting: UserSetting) => setting.name !== userPreferencesName);
                const prefValueObj = userPreferences.value as Record<string, unknown>;
                if (!hasSuggestedPagesSetting) {
                    delete prefValueObj.suggestedPagesTable;
                }
                if (
                    Object.keys(prefValueObj).includes('newTaggingCenter') ||
                    Object.keys(prefValueObj).includes('tagInASeparateBrowserWindow')
                ) {
                    delete prefValueObj.newTaggingCenter;
                    delete prefValueObj.tagInASeparateBrowserWindow;
                    await this.update({
                        name: 'userPreferences',
                        value: prefValueObj,
                        namespaced: true,
                        crossUI: true,
                    });
                }
                uniqueSettings.push(userPreferences);

                const allHistoryTypes = [
                    ...Object.values(HISTORY_TYPES),
                    ...Object.values(RECENTLY_SELECTED_HISTORY_TYPES),
                ];

                const historySettings = uniqueSettings.reduce((acc: Record<string, HistoryItem[]>, setting: UserSetting) => {
                    const [settingName, subId] = setting.name.split('--');

                    if (allHistoryTypes.includes(settingName) && subId === subscription.id) {
                        acc[settingName] = setting.value as HistoryItem[];
                    }

                    return acc;
                }, {} as Record<string, HistoryItem[]>);
                const settingsExcludingHistory = uniqueSettings.filter((setting: UserSetting) => {
                    const [settingName] = setting.name.split('--');

                    return !allHistoryTypes.includes(settingName);
                });

                this.map = keyBy(settingsExcludingHistory, 'name');
                useHistoryStore().setHistory({ history: historySettings });
            } catch (error) {
                loadAllPromise = undefined;
                this.error = error as Error;
            }

            this.fetching = false;
        },
        async loadOne (
            { reFetch = false, namespaced = false, name, crossUI = false }: {
                reFetch?: boolean;
                namespaced?: boolean;
                name: string;
                crossUI?: boolean;
            } = { name: '' },
        ) {
            if (!reFetch) await loadAllPromise;

            let settingName = name;
            if (namespaced) {
                // eslint-disable-next-line @typescript-eslint/no-require-imports
                const vuexStore = require('@/vuex/store').default;
                const { active: subscription } = vuexStore?.state.subscription;
                settingName = crossUI ? `${subscription.id}::${name}` : `${name}--${subscription.id}`;
            }

            if (this.map[settingName] && !reFetch) {
                return;
            }
            this.fetching = true;
            try {
                const setting = await fetchSetting({ name: settingName });
                const parsedValue = parseValue(setting.value);

                this.fetching = false;
                this.map[settingName] = {
                    name: setting.name,
                    value: parsedValue,
                };
            } catch (error) {
                this.fetching = false;
                this.error = error as Error;
                throw error;
            }
        },
        async update (
            { namespaced = false, name, value, force = false, crossUI = false }: {
                namespaced?: boolean;
                name: string;
                value: unknown;
                force?: boolean;
                crossUI?: boolean;
            },
        ) {
            let settingName = name;
            if (namespaced) {
                // eslint-disable-next-line @typescript-eslint/no-require-imports
                const vuexStore = require('@/vuex/store').default;
                const { active: subscription } = vuexStore?.state.subscription;
                settingName = crossUI ? `${subscription.id}::${name}` : `${name}--${subscription.id}`;
            }

            if (
                !force &&
                this.map[settingName] &&
                JSON.stringify(this.map[settingName].value) === JSON.stringify(value)
            ) {
                return;
            }

            let newValue = value;
            if (!isString(value)) {
                newValue = JSON.stringify(value);
            }

            try {
                const updatedSetting = await updateSetting({
                    name: settingName,
                    value: newValue,
                    userId: '{{uid}}',
                });

                this.map[updatedSetting.name] = {
                    name: updatedSetting.name,
                    value: parseValue(updatedSetting.value),
                };
            } catch (error) {
                this.fetching = false;
                this.error = error as Error;
                throw error;
            }
        },
        async remove ({ namespaced = false, name, crossUI = false }: { namespaced?: boolean; name: string; crossUI?: boolean }) {
            let settingName = name;
            if (namespaced) {
                // eslint-disable-next-line @typescript-eslint/no-require-imports
                const vuexStore = require('@/vuex/store').default;
                const { active: subscription } = vuexStore?.state.subscription;
                settingName = crossUI ? `${subscription.id}::${name}` : `${name}--${subscription.id}`;
            }

            this.fetching = true;
            try {
                await deleteSetting({ name: settingName });
                delete this.map[settingName];
            } catch (error) {
                this.error = error as Error;
                throw error;
            }
        },
        teardown () {
            loadAllPromise = undefined;
            Object.assign(this, getInitialState());
        },
    },
});

export function __resetLoadAllPromiseForUnitTests (): void {
    loadAllPromise = undefined;
}

if (module.hot) { module.hot.accept(acceptHMRUpdate(useUserSettingsStore, module.hot)); }
